import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
// v0.10.7 — toast 复用本仓既有的 SlotContextMenu.toast()
//   Flarum 2 前端没有 app.flash (app.flash 是静默 no-op), 本仓约定统一走
//   SlotContextMenu.toast() —— CalendarTab.js / SlotReservationRenderer.js 都走它,
//   所以这里**不再单开一个 toast 工具**: 合同 Modal 只是第三个调用方。
import SlotContextMenu from '../utils/SlotContextMenu';
// Phase D2 (v0.18.16): 合同上传改走 ziven-core 共享 uploadFileResult(), 随 storage_provider
// 自动切 COS / 七牛 / 本地。本调用点**必须**用 Result 版 (不是 uploadFile) —— 下面落库存的是
// key, 而本地档的 key 比云端多一层日期目录 (本地 {prefix}/{Ymd}/{ts}_{rand}.{ext},
// 云端 {prefix}/{ts}_{rand}.{ext}), 自己拼 key 必错。
import { uploadFileResult } from 'ext:ziiven/ziven-core/common/helpers/uploadFile';

/**
 * v0.10.7 — 学员合同 Modal (辉哥 10/8 拍板: 学员列表里的学员合同改用 Flarum 原生 modal)
 *
 * ## 为什么从自绘浮层搬到原生 Modal
 *   改前是 StudentContractPopover.js: 单例 + 纯 DOM + 挂 body 顶层 + getBoundingClientRect 手动定位,
 *   越界还要自己翻转, 上传时还得手动禁关闭 (自己监听 document 事件)。原生 Modal 全部自带:
 *   焦点陷阱 / 遮罩 / Esc / 动效 / z-index 层级, 越界和定位问题整个消失。本文件已删除那份浮层。
 *
 * ## 拍板落点 (承接 v0.10.0, 一条没丢)
 *   ① 合同挂学员 (user_school 关系), 不挂卡 → 一个学员一个合同集合
 *   ② 上传入口**只在学员列表**, 绑卡弹窗一行都不加 → 本文件只被 StudentsListTab 调
 *   ③ 非必填 → 空态只是上传入口, 不拦任何流程
 *   ④ 删除硬删 (COS 一并删) → 二次确认
 *   ⑥ COS 删失败保留 DB 行 → 后端抛错, 这里提示重试, Modal 保持打开
 *   ⑦ 合同名可改 → inline 编辑, 只改 display_name
 *
 * ## 实现约束
 *   - 事件属性一律小写 (onclick / oninput / onkeydown / ondrop)。
 *     Flarum 2 的 bundle 里 mithril 绑定是 `addEventListener(r.slice(2))`, **没有 toLowerCase**,
 *     `onClick` / `onDrop` 永不触发且不报错, 属于静默失效, 最难查。
 *   - PATCH / DELETE 走 mithril 直连, 不能用 app.request (见下方 rawRequest 注释)。
 */

// ── 硬约束 (与后端 StudentContractAccess 同口径) ──────────────────────────────
const MAX_SIZE = 20 * 1024 * 1024; // 单文件 20MB
const MAX_COUNT = 20; // 单学员 20 份
const MAX_NAME_LEN = 60; // 合同名 60 字符
const ALLOWED_EXTS = ['pdf', 'jpg', 'jpeg', 'png', 'webp'];

/**
 * 上传中的关闭锁 —— 必须是**模块级**, 不能挂在实例上。
 *
 * 原因 (读 Flarum 2 ModalManager 源码得出, 别想当然改回实例字段):
 *   Esc / 遮罩点击是 ModalManager 通过 `componentClass.dismissibleOptions` 读的,
 *   那个是**静态 getter**, ModalManager 拿不到 Modal 实例, 所以「上传中禁关闭」
 *   没法靠实例状态表达, 只能让静态 getter 去读一个模块级计数。
 *   ModalManager 每次按键/每次点击都重新读一次这个 getter, 所以是实时生效的。
 *
 * 同理 X 按钮 (Modal.view 里读的是实例 getter) 也由这个计数驱动, 保证三条关闭路径一致。
 */
let uploadLock = 0;

/** 体积文案 —— 报错必须带**实际大小**(拍板: 不要只写「上传失败」) */
function humanSize(bytes) {
  if (bytes >= 1024 * 1024) return Math.round(bytes / 1024 / 1024 * 10) / 10 + ' MB';
  if (bytes >= 1024) return Math.round(bytes / 1024) + ' KB';
  return bytes + ' B';
}

/** API 前缀 */
function apiBase() {
  return app.forum.attribute('apiUrl') + '/ziiven/scheduler/student-contracts';
}

/**
 * ⚠️ DELETE 跟 PATCH 都必须走 mithril 直连, 不能用 app.request ——
 *   app.request 会把 PATCH/DELETE 改写成 POST (Api.js 里有同款注释: 走了会 405)。
 *   跟 Api.js 的 update/delete 完全一致。
 */
function rawRequest(method, url, body) {
  const m = typeof window !== 'undefined' ? window.m : null;
  const opts = {
    method,
    url,
    headers: {
      'X-CSRF-Token': app.session.csrfToken,
      'X-Requested-With': 'XMLHttpRequest'
    }
  };
  if (body !== undefined) {
    opts.body = body;
    opts.serialize = x => JSON.stringify(x);
    opts.headers['Content-Type'] = 'application/json';
  }
  if (m && typeof m.request === 'function') return m.request(opts);
  return app.request(opts);
}
export default class StudentContractModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    const s = vnode.attrs.student || {};
    this.studentId = parseInt(s.user_id, 10) || 0;
    this.studentName = s.display_name || s.nickname || '学员';
    this.schoolId = parseInt(vnode.attrs.schoolId, 10) || 0;
    this.onChanged = vnode.attrs.onChanged;
    this.contracts = [];
    this.canUploadMore = true;
    this.loading = true;
    this.uploading = false; // 有文件正在传 → 禁用关闭
    this.editingId = null; // 正在改名的合同 id
    this.draftName = ''; // 改名草稿
    this.deleting = false; // 删除请求在飞
    this.confirmFor = null; // 正在二次确认删除的合同对象

    this.load();
  }
  onremove() {
    // 计数/监听是模块级 + document 级的, 这里兜底释放 ——
    // 万一上传途中组件被卸载, 不能把后续所有合同 Modal 的关闭能力永久锁死。
    uploadLock = 0;
    this.releaseEscBlock();
  }
  className() {
    // v0.10.7b — 补 Flarum 尺寸类, 跟 AddStudentModal / EditStudentModal 对齐
    return 'Modal--medium Modal--fullscreen-padded ZS-StudentContractModal';
  }

  /**
   * 上传中禁关闭 —— 静态 getter 覆盖 **X 按钮 + 遮罩点击** 两条路径。
   * 计数 >0 时两条全关, 跟改前自绘浮层「上传中禁用关闭」的行为一致。
   *
   * ⚠️ **Esc 不归它管** (实测踩过): ModalManager 的 handleEscPress 开头是
   *    `if (closeWatcher && closeWatcher.onclose) return;` —— Chrome 126+ 有
   *    `CloseWatcher` (本机实测 'CloseWatcher' in window === true), Flarum 在
   *    **打开时**就把它接上了, 于是 Esc 走 CloseWatcher → animateHide() 直连,
   *    **绕过了下面这个实时 getter**, 改 viaEscKey 根本不生效 (实测: 上传中按 Esc
   *    弹窗照关不误, 而 X 按钮和遮罩都锁住了)。
   *    Esc 由 blockEscWhileUploading() 单独拦, 见该方法注释。
   */
  static get dismissibleOptions() {
    const busy = uploadLock > 0;
    return {
      viaCloseButton: !busy,
      viaEscKey: !busy,
      viaBackdropClick: !busy
    };
  }

  /**
   * 上传中拦 Esc —— 关闭锁的第三条路径, 必须单写, 原因见上面 dismissibleOptions。
   *
   * 两条 capture 监听, 挂在 document 上 (ModalManager 自己的 keyup 挂在
   * document.body 的**冒泡**阶段, document 的**捕获**阶段一定先跑):
   *   keydown: preventDefault —— CloseWatcher 是 UA 级的关闭请求, 由 Esc 的
   *            **keydown** 触发; 按规范 keydown 被 cancel 掉时就不该关, 这是
   *            唯一能从 JS 侧掐掉 CloseWatcher 的办法。
   *   keyup:   stopPropagation —— 掐掉 Flarum 的 handleEscPress。
   * 两个监听都只在**上传期间**存在 (加锁时挂, 解锁时摘), 不影响其它时刻的 Esc。
   */
  blockEscWhileUploading() {
    if (this._escBlocker) return;
    // 必须是**同一个函数引用**, removeEventListener 靠引用匹配, 换引用摘不掉
    const block = e => {
      if (e.key !== 'Escape') return;
      e.preventDefault(); // 掐掉 CloseWatcher 的关闭请求
      e.stopPropagation(); // 掐掉 ModalManager 的 handleEscPress
    };
    this._escBlocker = block;
    document.addEventListener('keydown', block, true);
    document.addEventListener('keyup', block, true);
  }
  releaseEscBlock() {
    if (!this._escBlocker) return;
    document.removeEventListener('keydown', this._escBlocker, true);
    document.removeEventListener('keyup', this._escBlocker, true);
    this._escBlocker = null;
  }
  title() {
    const n = this.contracts.length;
    return m("span", null, m("i", {
      className: "fas fa-paperclip ZS-contract-tico",
      "aria-hidden": "true"
    }), this.studentName, " \u7684\u5408\u540C", n > 0 && m("span", {
      className: "ZS-contract-tcount"
    }, "\xB7 ", n, " \u4EFD"));
  }

  // ── 数据 ──────────────────────────────────────────────────────────────────

  load() {
    this.loading = true;
    m.redraw();
    return app.request({
      method: 'GET',
      url: apiBase(),
      params: {
        user_id: this.studentId,
        school_id: this.schoolId
      }
    }).then(resp => {
      this.contracts = resp && resp.contracts || [];
      this.canUploadMore = !resp || resp.can_upload_more !== false;
      this.loading = false;
      m.redraw();
      this.notify(this.contracts.length);
    }).catch(err => {
      // ⚠️ 这里之前只有一句「列表加载失败」, 把**真实异常**吞掉了 ——
      //   渲染里的报错也会落进同一个 catch (then 里抛的), 表现成「接口失败」,
      //   排查时完全指不到真正的位置。必须留痕。
      console.error('[student-contract] loadContracts failed', err);
      this.contracts = [];
      this.canUploadMore = true;
      this.loading = false;
      m.redraw();
      this.toast('合同列表加载失败', 'error');
    });
  }

  /** 上传/改名/删除后通知外部 (StudentsListTab 靠它刷新角标份数) */
  notify(count) {
    if (this.onChanged) this.onChanged(typeof count === 'number' ? count : undefined);
  }
  toast(msg, kind) {
    if (kind === void 0) {
      kind = 'success';
    }
    SlotContextMenu.toast(msg, kind);
  }

  // ── 预览 / 下载 ───────────────────────────────────────────────────────────

  /**
   * 缩略图 / 预览都用 preview_url —— v0.10.0a (修 P0-3)
   *
   * ⚠️ v0.10.2: **`|| ct.file_url` 兜底不存在, 也不能加回来**。
   *   file_url 存的是 COS **key**, 塞进 <img src> 会被当相对路径解析成
   *   http://<本站>/scheduler_contract/xxx.pdf → 404 → 浏览器破图标。
   *   preview_url 由后端 ListStudentContractsController 用 StudentContractAccess::fileUrl()
   *   (内部 TencentCOSService::buildCdnUrl) 算好下发; 后端返回 null 时前端就**如实返回 null**,
   *   由渲染方出占位块, 而不是自己造一个注定打不开的 URL。
   *
   * @return {string|null} null = 无可用预览 (渲染占位块, 不建 <img>)
   */
  thumbUrl(ct) {
    return ct.preview_url || null;
  }
  preview(ct) {
    const u = this.thumbUrl(ct);
    if (u) window.open(u, '_blank', 'noopener');else this.toast('预览不可用（COS 未配置或文件无法访问）', 'error');
  }

  /** 下载 (8/8 澄清: 用 display_name 作为存盘名, COS 对象不动) */
  download(ct) {
    // 走本扩展的 download 端点而不是直接指 CDN:
    //   `download` 属性只在同源生效, CDN 是跨源 → 浏览器会忽略它,
    //   存盘名退回 COS key 的 basename (一串时间戳+随机串), 改名等于白做。
    //   由后端转发并带 Content-Disposition: attachment; filename*=UTF-8''<display_name>
    const a = document.createElement('a');
    a.href = apiBase() + '/' + ct.id + '/download';
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      if (a.parentNode) a.parentNode.removeChild(a);
    }, 1000);
  }

  // ── 改名 (拍板⑦ + 8/8 澄清: 只改 display_name) ────────────────────────────

  startRename(ct) {
    this.editingId = ct.id;
    this.draftName = ct.display_name || '';
    m.redraw();
    // 自动聚焦 + 全选 (改名是高频操作, 少一次点击)
    setTimeout(() => {
      const el = this.element && this.element.querySelector('.ZS-contract-edit');
      if (el) {
        el.focus();
        el.select();
      }
    }, 0);
  }
  cancelRename() {
    this.editingId = null;
    this.draftName = '';
    m.redraw();
  }
  saveRename(ct) {
    if (!ct) return;
    const name = String(this.draftName || '').trim();
    if (!name) {
      this.toast('合同名不能为空', 'error');
      return;
    }
    if (name.length > MAX_NAME_LEN) {
      this.toast('合同名不能超过 ' + MAX_NAME_LEN + ' 个字符（当前 ' + name.length + ' 字）', 'error');
      return;
    }
    if (name === ct.display_name) {
      this.cancelRename();
      return;
    }
    return rawRequest('PATCH', apiBase() + '/' + ct.id, {
      data: {
        attributes: {
          display_name: name
        }
      }
    }).then(resp => {
      ct.display_name = name;
      this.editingId = null;
      this.draftName = '';
      m.redraw();
      // 后端把「未变动字段」原样回传 —— 当场自检: 有一项被动过就是 bug
      if (resp && resp.file_url !== undefined) {
        const drift = [];
        if (resp.file_url !== ct.file_url) drift.push('file_url');
        if (resp.original_name !== ct.original_name) drift.push('original_name');
        if (String(resp.size) !== String(ct.size)) drift.push('size');
        if (resp.mime_type !== ct.mime_type) drift.push('mime_type');
        if (resp.created_at !== ct.created_at) drift.push('created_at');
        if (drift.length) {
          console.error('[student-contract] 改名后以下字段被改动, 违反拍板⑦: ' + drift.join(', '));
          this.toast('改名异常，部分字段被改动', 'error');
        }
      }
      this.toast('已改名');
      this.notify();
    }).catch(err => {
      const msg = err && err.message || '改名失败，请重试';
      this.toast(msg, 'error');
    });
  }

  // ── 删除二次确认 (拍板④ 硬删 + ⑥ 方案 A) ─────────────────────────────────

  doDelete(ct) {
    this.deleting = true;
    m.redraw();
    return rawRequest('DELETE', apiBase() + '/' + ct.id).then(() => {
      this.contracts = this.contracts.filter(x => x.id !== ct.id);
      this.deleting = false;
      this.confirmFor = null;
      m.redraw();
      this.toast('已删除');
      this.notify(this.contracts.length);
    }).catch(err => {
      // 拍板⑥ —— 后端在 COS 删失败时抛「删除失败，请重试」, DB 行原样保留
      this.deleting = false;
      m.redraw();
      const msg = err && err.message || '删除失败，请重试';
      this.toast(msg, 'error');
      throw err;
    });
  }

  // ── 上传 ──────────────────────────────────────────────────────────────────

  handleFiles(files) {
    const have = this.contracts.length;
    if (this.canUploadMore === false || have + files.length > MAX_COUNT) {
      this.toast('每个学员最多 ' + MAX_COUNT + ' 份合同（当前已有 ' + have + ' 份）', 'error');
      return;
    }

    // v0.18.11 — 改前这里有 `if (!this.uploadOpen) { this.uploadOpen = true; }`:
    //   拖拽区本来是「点按钮展开」的, 拖进来时顺手展开一下。
    //   本轮拖拽区改成**桌面端常驻**, 不再由按钮控制 ⇒ 这行连同 uploadOpen 状态一起删掉。

    // 先做本地校验 (只为即时反馈), 后端落库时还会再校验一遍
    const rejected = [];
    const accepted = [];
    files.forEach(f => {
      const ext = (f.name.split('.').pop() || '').toLowerCase();
      if (!ALLOWED_EXTS.includes(ext)) {
        rejected.push(f.name + '：仅支持 pdf / jpg / jpeg / png / webp 格式');
        return;
      }
      if (f.size > MAX_SIZE) {
        // 拍板: 报**实际大小**, 不要写「上传失败」
        rejected.push(f.name + ' 超过 20MB（当前 ' + humanSize(f.size) + '）');
        return;
      }
      accepted.push(f);
    });
    if (rejected.length) {
      this.toast(rejected[0], 'error');
      if (accepted.length === 0) {
        m.redraw();
        return;
      }
    }
    this.uploading = true;
    uploadLock++; // 上传中禁关闭 (X / 遮罩走静态 getter)
    this.blockEscWhileUploading(); // Esc 走 CloseWatcher, 得单独掐
    m.redraw();

    // 串行上传, 避免同时打多个 COS 请求
    const chain = accepted.reduce((p, f) => p.then(() => this.uploadOne(f)), Promise.resolve());
    return chain.then(() => {
      this.uploading = false;
      this.toast('上传完成');
      return this.load();
    }).catch(() => {
      this.uploading = false;
      m.redraw();
    }).then(() => {
      uploadLock = Math.max(0, uploadLock - 1);
      this.releaseEscBlock();
      m.redraw();
    });
  }

  /**
   * 上传一个合同文件并落库。
   *
   * Phase D2 (v0.18.16): 原来的「拿 scheduler 自己的 COS 签名 → 拼 FormData → XHR 直传」
   * 已删除, 改用 ziven-core 的 uploadFileResult()。
   * 🔴 落库的 `file_url` 存的**仍然是 key** (不是 URL), 跟改造前一致 —— 上传时机硬约束
   *    也保持不变: **先传完、再落库**。
   *    key 必须取返回值, 不能自己拼: 本地档的 key 多一层 `{Ymd}/` 日期目录。
   */
  uploadOne(file) {
    if (typeof uploadFileResult !== 'function') {
      return Promise.reject(new Error('ziven-core 上传组件未就绪, 请刷新页面后重试'));
    }
    return uploadFileResult(file, {
      prefix: 'scheduler_contract'
    }).then(res => {
      const key = res && res.key;
      if (!key) {
        throw new Error('上传成功但未返回存储 key, 请重试');
      }
      return app.request({
        method: 'POST',
        url: apiBase(),
        body: {
          data: {
            attributes: {
              user_id: this.studentId,
              school_id: this.schoolId,
              file_url: key,
              // 存 key, 不是完整 URL
              original_name: file.name,
              size: file.size,
              mime_type: file.type || ''
            }
          }
        },
        serialize: x => JSON.stringify(x),
        headers: {
          'Content-Type': 'application/json'
        }
      });
    }).catch(err => {
      const msg = err && err.message || '上传失败，请重试';
      this.toast(msg, 'error');
      throw err;
    });
  }

  // ── 渲染 ──────────────────────────────────────────────────────────────────

  content() {
    if (this.loading) {
      return m("div", {
        className: "Modal-body"
      }, m("div", {
        className: "ZS-contract-loading"
      }, "\u6B63\u5728\u52A0\u8F7D\u5408\u540C\u2026"));
    }
    if (this.confirmFor) return this.confirmView();
    const list = this.contracts;
    // v0.18.11 — 改前是 `!list.length && !this.uploadOpen` (拖拽区展开时不算空态)。
    //   本轮拖拽区桌面端常驻、移动端 CSS 隐藏, 不再是「用户主动展开」的状态 ⇒ 只看有没有合同。
    const empty = !list.length;
    return m("div", {
      className: "Modal-body"
    }, empty ? m("div", {
      className: "ZS-contract-empty"
    }, m("div", {
      className: "ZS-contract-empty-i"
    }, m("i", {
      className: "fas fa-paperclip",
      "aria-hidden": "true"
    })), "\u8FD8\u6CA1\u6709\u5408\u540C", m("br", null), m("span", {
      className: "ZS-contract-empty-s"
    }, "\u53EF\u4EE5\u968F\u65F6\u8865\u4F20\uFF0C\u4E0D\u5F71\u54CD\u7ED1\u5361")) : m("div", {
      className: "ZS-contract-list"
    }, this.uploadView(), list.map(ct => this.rowView(ct))), m("input", {
      className: "ZS-contract-file",
      type: "file",
      accept: ".pdf,.jpg,.jpeg,.png,.webp",
      multiple: true,
      style: "display:none",
      onchange: e => {
        const t = e.target;
        if (t.files && t.files.length) this.handleFiles(Array.prototype.slice.call(t.files));
        t.value = '';
      }
    }), m("div", {
      className: "ZS-contract-foot"
    }, this.uploading ? m("button", {
      className: "ZS-btn",
      disabled: true
    }, "\u4E0A\u4F20\u4E2D\u2026")
    /* v0.18.11 (辉哥 10/18: 「点击上传按钮后就应该弹出上传文件选择框」) ——
       改前是 `this.uploadOpen = !this.uploadOpen; m.redraw()` —— 点一下只展开拖拽区,
       还要**再点那块**才弹文件框, 两步。本轮改成一步: 直接 click 顶层那个 hidden input。
       ⛔ 不要再改回切换 uploadOpen —— 该状态本轮已整体删除 (见 oninit / handleFiles)。 */ : m("button", {
      className: "ZS-btn",
      onclick: () => {
        const input = this.element && this.element.querySelector('.ZS-contract-file');
        if (input) input.click();
      }
    }, "\u4E0A\u4F20\u5408\u540C")));
  }
  uploadView() {
    return m("div", {
      className: 'ZS-contract-drop' + (this.uploading ? ' is-busy' : '') + (this.dropHot ? ' is-hot' : ''),
      onclick: () => {
        if (this.uploading) return;
        // input 已移到 content() 的 Modal-body 顶层 (v0.18.11), 这里 querySelector 照常找得到。
        const input = this.element && this.element.querySelector('.ZS-contract-file');
        if (input) input.click();
      },
      ondragenter: e => {
        e.preventDefault();
        this.dropHot = true;
        m.redraw();
      },
      ondragover: e => {
        e.preventDefault();
        this.dropHot = true;
        m.redraw();
      },
      ondragleave: e => {
        e.preventDefault();
        this.dropHot = false;
        m.redraw();
      },
      ondrop: e => {
        e.preventDefault();
        this.dropHot = false;
        if (this.uploading) return;
        const files = e.dataTransfer && e.dataTransfer.files;
        if (files && files.length) this.handleFiles(Array.prototype.slice.call(files));
        m.redraw();
      }
    }, m("div", {
      className: "ZS-contract-drop-ic"
    }, m("i", {
      className: "fas fa-upload",
      "aria-hidden": "true"
    })), m("div", {
      className: "ZS-contract-drop-t"
    }, "\u70B9\u51FB\u9009\u62E9\uFF0C\u6216\u628A\u5408\u540C\u62D6\u5230\u8FD9\u91CC"), m("div", {
      className: "ZS-contract-drop-s"
    }, "PDF / JPG / PNG / WEBP \xB7 \u5355\u4E2A \u2264 20MB \xB7 \u6700\u591A 20 \u4EFD"));
  }
  rowView(ct) {
    const editing = this.editingId === ct.id;
    if (editing) {
      const len = (this.draftName || '').length;
      return m("div", {
        className: "ZS-contract-row is-editing"
      }, m("div", {
        className: ct.is_pdf ? 'ZS-contract-pdf' : 'ZS-contract-thumb'
      }, ct.is_pdf ? 'PDF' : ''), m("div", {
        className: "ZS-contract-meta"
      }, m("input", {
        className: "ZS-contract-edit",
        maxlength: MAX_NAME_LEN,
        value: this.draftName,
        oninput: e => {
          this.draftName = e.target.value;
          m.redraw();
        },
        onkeydown: e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.saveRename(ct);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            this.cancelRename();
          }
        },
        onkeyup: e => {
          e.stopPropagation();
        }
      }), m("div", {
        className: "ZS-contract-editbar"
      }, m("span", {
        className: "ZS-contract-ok",
        onclick: () => this.saveRename(ct)
      }, m("i", {
        className: "fas fa-check",
        "aria-hidden": "true"
      }), " \u4FDD\u5B58"), m("span", {
        className: "ZS-contract-no",
        onclick: () => this.cancelRename()
      }, m("i", {
        className: "fas fa-xmark",
        "aria-hidden": "true"
      }), " \u53D6\u6D88"), m("span", {
        className: "ZS-contract-count"
      }, len, "/", MAX_NAME_LEN))));
    }

    // v0.10.2 (修 P0-3) — preview_url 为 null 时**不建 <img>**, 改渲染占位块。
    //   改前无条件建 <img src>, 配置缺失时会出现浏览器破图标 (实测)。
    const thumbUrl = ct.is_pdf ? null : this.thumbUrl(ct);
    const thumb = ct.is_pdf ? m("div", {
      className: "ZS-contract-pdf"
    }, "PDF") : thumbUrl ? m("img", {
      className: "ZS-contract-thumb",
      src: thumbUrl,
      alt: ""
    }) : m("div", {
      className: "ZS-contract-pdf is-nopreview",
      title: "\u9884\u89C8\u4E0D\u53EF\u7528"
    }, "\u2014");
    const sub = (ct.created_at || '').slice(0, 16) + ' · ' + humanSize(ct.size);
    return m("div", {
      className: "ZS-contract-row"
    }, thumb, m("div", {
      className: "ZS-contract-meta"
    }, m("div", {
      className: "ZS-contract-name"
    }, m("b", {
      title: ct.display_name
    }, ct.display_name), m("span", {
      className: "ZS-contract-pen",
      title: "\u6539\u540D",
      onclick: () => this.startRename(ct)
    }, m("i", {
      className: "fas fa-pen-to-square",
      "aria-hidden": "true"
    }))), m("div", {
      className: "ZS-contract-sub"
    }, sub)), m("div", {
      className: "ZS-contract-acts"
    }, m("button", {
      className: "ZS-contract-pbtn",
      title: "\u9884\u89C8",
      onclick: () => this.preview(ct)
    }, m("i", {
      className: "fas fa-eye",
      "aria-hidden": "true"
    })), m("button", {
      className: "ZS-contract-pbtn",
      title: "\u4E0B\u8F7D",
      onclick: () => this.download(ct)
    }, m("i", {
      className: "fas fa-download",
      "aria-hidden": "true"
    })), m("button", {
      className: "ZS-contract-pbtn is-del",
      title: "\u5220\u9664",
      onclick: () => {
        this.confirmFor = ct;
        m.redraw();
      }
    }, m("i", {
      className: "fas fa-trash",
      "aria-hidden": "true"
    }))));
  }

  /**
   * 删除二次确认 —— Flarum 2 没有自带 ConfirmationModal (bundle 里搜不到),
   * 所以确认框就画在本 Modal 内部, 不叠第二层弹窗。
   */
  confirmView() {
    const ct = this.confirmFor;
    return m("div", {
      className: "Modal-body ZS-contract-cfbody"
    }, m("div", {
      className: "ZS-contract-cf-hd"
    }, m("i", {
      className: "fas fa-triangle-exclamation",
      "aria-hidden": "true"
    }), m("span", null, "\u5220\u9664\u5408\u540C")), m("div", {
      className: "ZS-contract-cf-q"
    }, "\u786E\u5B9A\u8981\u5220\u9664\u8FD9\u4EFD\u5408\u540C\u5417\uFF1F"), m("div", {
      className: "ZS-contract-chip"
    }, m("i", {
      className: "fas fa-file-lines",
      "aria-hidden": "true"
    }), " ", ct.display_name, " \xB7 ", humanSize(ct.size)), m("div", {
      className: "ZS-contract-cf-warn"
    }, m("b", null, "COS \u4E0A\u7684\u6587\u4EF6\u4F1A\u4E00\u5E76\u5220\u9664\uFF0C\u65E0\u6CD5\u6062\u590D\u3002"), m("br", null), "\u5982\u679C\u53EA\u662F\u4E0D\u60F3\u770B\u5230\u5B83\uFF0C\u5EFA\u8BAE", m("b", null, "\u5148\u4E0B\u8F7D\u5907\u4EFD"), "\u518D\u5220\u3002"), m("ul", {
      className: "ZS-contract-cf-list"
    }, m("li", null, "\u5220\u9664\u540E\u8BE5\u5408\u540C\u7ACB\u5373\u4ECE\u5217\u8868\u6D88\u5931"), m("li", null, "\u64CD\u4F5C\u4F1A\u5199\u5165\u64CD\u4F5C\u65E5\u5FD7\uFF08\u8C01\u5220\u7684 / \u54EA\u4E2A\u5B66\u5458 / \u4EC0\u4E48\u65F6\u5019\uFF09"), m("li", null, "\u6B64\u64CD\u4F5C", m("b", null, "\u4E0D\u53EF\u64A4\u9500"), "\uFF0C\u6CA1\u6709\u56DE\u6536\u7AD9")), m("div", {
      className: "ZS-contract-cf-ft"
    }, m("button", {
      className: "ZS-contract-cbtn",
      onclick: () => this.download(ct)
    }, "\u5148\u4E0B\u8F7D\u5907\u4EFD"), m("button", {
      className: "ZS-contract-cbtn",
      onclick: () => {
        this.confirmFor = null;
        m.redraw();
      }
    }, "\u53D6\u6D88"), m("button", {
      className: "ZS-contract-cbtn is-dan",
      disabled: this.deleting,
      onclick: () => this.doDelete(ct)
    }, this.deleting ? '删除中…' : '确认删除')));
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/modal/StudentContractModal', StudentContractModal);